/**
 * Permission Management Hooks
 * Coláiste Lurgan Management System - Permission and user management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from './use-toast';
import { apiClient } from '../lib/api';

// Real API functions connected to Django backend
const permissionAPI = {
  // User management
  getUsers: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.user_type) params.append('user_type', filters.user_type);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
    if (filters.is_verified !== undefined) params.append('is_verified', filters.is_verified);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page);
    if (filters.page_size) params.append('page_size', filters.page_size);

    const response = await apiClient.get(`/api/auth/users/?${params.toString()}`);
    return response.data;
  },

  getUserStats: async () => {
    const response = await apiClient.get('/api/auth/users/stats/');
    return response.data;
  },

  // Permission management  
  getPermissions: async (filters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.category) params.append('category', filters.category);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
    if (filters.search) params.append('search', filters.search);
    
    const response = await apiClient.get(`/api/auth/permissions/?${params.toString()}`);
    return response.data;
  },

  // Role permissions
  getRolePermissions: async () => {
    const response = await apiClient.get('/api/auth/role-permissions/');
    return response.data;
  },

  getRolePermissionsByType: async (userType) => {
    const response = await apiClient.get(`/api/auth/role-permissions/role-permissions/${userType}/`);
    return response.data;
  },

  setRolePermissions: async (userType, permissionIds) => {
    const response = await apiClient.post('/api/auth/role-permissions/set-role-permissions/', {
      user_type: userType,
      permissions: permissionIds
    });
    return response.data;
  },

  // Current user permissions
  getMyPermissions: async () => {
    const response = await apiClient.get('/api/auth/my-permissions/');
    return response.data;
  },

  // User operations
  createUser: async (userData) => {
    const response = await apiClient.post('/api/auth/users/', userData);
    return response.data;
  },

  updateUser: async (userId, userData) => {
    const response = await apiClient.patch(`/api/auth/users/${userId}/`, userData);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await apiClient.delete(`/api/auth/users/${userId}/`);
    return response.data;
  },

  getUserById: async (userId) => {
    const response = await apiClient.get(`/api/auth/users/${userId}/`);
    return response.data;
  },

  resendVerification: async (userId) => {
    const response = await apiClient.post(`/api/auth/users/${userId}/resend-verification/`);
    return response.data;
  },

  getExpiredParentVerificationsCount: async () => {
    const response = await apiClient.get('/api/auth/users/expired-parent-verifications/');
    return response.data;
  },

  resendExpiredParentVerifications: async () => {
    const response = await apiClient.post('/api/auth/users/resend-expired-verifications/');
    return response.data;
  }
};

// Query keys for caching
const queryKeys = {
  users: {
    all: ['users'],
    list: (filters) => ['users', 'list', filters],
    detail: (id) => ['users', 'detail', id],
  },
  permissions: {
    all: ['permissions'],
    list: (filters) => ['permissions', 'list', filters],
  },
  rolePermissions: {
    all: ['rolePermissions'],
    byRole: (roleId) => ['rolePermissions', roleId],
  }
};

// Users hooks
export function useUsers(filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.users.list(filters),
    queryFn: () => permissionAPI.getUsers(filters),
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  });
}

export function useUserStats(options = {}) {
  return useQuery({
    queryKey: ['userStats'],
    queryFn: () => permissionAPI.getUserStats(),
    staleTime: 60 * 1000, // 1 minute
    ...options,
  });
}

export function useUser(userId, options = {}) {
  return useQuery({
    queryKey: queryKeys.users.detail(userId),
    queryFn: () => permissionAPI.getUserById(userId),
    enabled: !!userId,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useCreateUser(options = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: permissionAPI.createUser,
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast({
        title: 'Úsáideoir Cruthaithe / User Created',
        description: 'Cruthaíodh úsáideoir nua go rathúil / New user has been created successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Earráid / Error',
        description: error.response?.data?.message || 'Theip ar chruthú úsáideora / Failed to create user. Please try again.',
        variant: 'destructive',
      });
    },
    ...options,
  });
}

export function useUpdateUser(options = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, ...data }) => permissionAPI.updateUser(id, data),
    onSuccess: (data, variables) => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.id) });
      
      toast({
        title: 'Úsáideoir Nuashonraithe / User Updated',
        description: 'Nuashonraíodh faisnéis an úsáideora go rathúil / User information has been updated successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Earráid / Error',
        description: error.response?.data?.message || 'Theip ar nuashonrú úsáideora / Failed to update user. Please try again.',
        variant: 'destructive',
      });
    },
    ...options,
  });
}

export function useDeleteUser(options = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: permissionAPI.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast({
        title: 'Úsáideoir Scriosadh / User Deleted',
        description: 'Baineadh an t-úsáideoir ón gcóras / User has been removed from the system.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Earráid / Error',
        description: error.response?.data?.message || 'Theip ar scriosadh úsáideora / Failed to delete user. Please try again.',
        variant: 'destructive',
      });
    },
    ...options,
  });
}

export function useResendVerification(options = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: permissionAPI.resendVerification,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast({
        title: options.successTitle || 'Verification Email Sent',
        description: data?.message || 'Verification email has been resent.',
      });
    },
    onError: (error) => {
      toast({
        title: options.errorTitle || 'Error',
        description: error.response?.data?.error || 'Failed to resend verification email.',
        variant: 'destructive',
      });
    },
    ...options,
  });
}

export function useExpiredParentVerificationsCount(options = {}) {
  return useMutation({
    mutationFn: permissionAPI.getExpiredParentVerificationsCount,
    ...options,
  });
}

export function useResendExpiredParentVerifications(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: permissionAPI.resendExpiredParentVerifications,
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      options.onSuccess?.(data, variables, context);
    },
  });
}

// Permissions hooks
export function usePermissions(filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.permissions.list(filters),
    queryFn: () => permissionAPI.getPermissions(filters),
    staleTime: 60 * 1000, // 1 minute - permissions don't change often
    ...options,
  });
}

// Role permissions hooks
export function useRolePermissions(options = {}) {
  return useQuery({
    queryKey: queryKeys.rolePermissions.all,
    queryFn: permissionAPI.getRolePermissions,
    staleTime: 60 * 1000, // 1 minute
    ...options,
  });
}

export function useRolePermissionsByType(userType, options = {}) {
  return useQuery({
    queryKey: queryKeys.rolePermissions.byRole(userType),
    queryFn: () => permissionAPI.getRolePermissionsByType(userType),
    enabled: !!userType,
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useSetRolePermissions(options = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ userType, permissionIds }) => permissionAPI.setRolePermissions(userType, permissionIds),
    onSuccess: (data, variables) => {
      // Invalidate role permissions queries
      queryClient.invalidateQueries({ queryKey: queryKeys.rolePermissions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.rolePermissions.byRole(variables.userType) });
      
      toast({
        title: 'Permissions Updated',
        description: `Role permissions for ${variables.userType} have been updated successfully.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update permissions. Please try again.',
        variant: 'destructive',
      });
    },
    ...options,
  });
}

// Current user permissions
export function useMyPermissions(options = {}) {
  return useQuery({
    queryKey: ['myPermissions'],
    queryFn: permissionAPI.getMyPermissions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// Field-level permissions for current user (for filter visibility)
export function useMyFieldPermissions(options = {}) {
  return useQuery({
    queryKey: ['myFieldPermissions'],
    queryFn: async () => {
      const response = await apiClient.get('/api/students/permissions/field-permissions/my_fields/');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - matches backend cache
    ...options,
  });
}

// Utility hooks for permission checking in components
export function useCanManageUsers() {
  const { data: myPermissions } = useMyPermissions();
  return myPermissions?.permissions?.some(p => p.name === 'manage_users') || false;
}

export function useCanManagePermissions() {
  const { data: myPermissions } = useMyPermissions();
  return myPermissions?.permissions?.some(p => p.name === 'manage_permissions') || false;
}

const permissionHooks = {
  useUsers,
  useUserStats,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  usePermissions,
  useRolePermissions,
  useSetRolePermissions,
  useCanManageUsers,
  useCanManagePermissions,
  useMyFieldPermissions
};

export default permissionHooks;